import React from 'react';
import { useLocation } from 'react-router-dom';
import { Chip } from '@mui/material';
import { STATUS_META } from '../../services/expenseVouchersApi';
import { PAYMENT_STATUS_META } from '../../services/expenseAccountsApi';

// Expense screens are mounted under both layouts (/expenses and /admin/expenses).
export const useExpenseBase = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin/') ? '/admin/expenses' : '/expenses';
};

export const useApprovalsBase = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin/') ? '/admin/expense-approvals' : '/expense-approvals';
};

export const useAccountsBase = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/admin/') ? '/admin/expense-accounts' : '/expense-accounts';
};

const PAYMENT_TYPE_LABELS = {
  ADVANCED: 'Advanced',
  NEW_ADVANCED: 'New Advanced',
  SETTLEMENT: 'Settlement',
  REIMBURSABLE: 'Reimbursable',
  FINAL_PAYMENT: 'Final Payment',
};

// erp_expense shows the card / account itself as the payment type
export const paymentTypeLabel = (l) => (l.paymentType === 'PAID_VIA' ? l.paidViaName : PAYMENT_TYPE_LABELS[l.paymentType]) || '-';

export const VoucherStatusChip = ({ status, size = 'small' }) => {
  const meta = STATUS_META[status] || { label: status, color: 'default' };
  return <Chip size={size} label={meta.label} color={meta.color} variant={status === 'DRAFT' ? 'outlined' : 'filled'} />;
};

export const GROUP_LABELS = { REPORTING: 'Reporting', MANAGEMENT: 'Management' };

export const ROUTE_LABELS = {
  REPORTING: 'Reporting Manager',
  MANAGEMENT: 'Management',
  BOTH: 'Both (Reporting + Management)',
  FINALIZE: 'Finalize',
};

// "Approved (Reporting)", "Routed by Accounts: Both (Reporting + Management)"
export const historyLabel = (h) => {
  const base = HISTORY_LABELS[h.action] || h.action;
  if (h.action === 'ROUTED' && h.route) return `${base}: ${ROUTE_LABELS[h.route] || h.route}`;
  if (h.group) return `${base} (${GROUP_LABELS[h.group] || h.group})`;
  return base;
};

export const vendorLabel = (vendor) => {
  if (!vendor) return '-';
  if (vendor.kind === 'SELF') return `${vendor.name || 'Self'} (Internal)`;
  if (vendor.kind === 'EMPLOYEE') return `${vendor.name || 'Employee'} (Internal)`;
  return vendor.name || '-';
};

// A PAID voucher completed without any cash payment (Phase 5B-1A: Net Payable 0, no
// payment row): `settledVia` WALLET / NO_PAYMENT. CASH (or absent) is a real payment.
export const SETTLED_VIA_META = {
  WALLET: { label: 'Settled (wallet)', hint: 'Settled against the employee wallet. No payment was made.' },
  NO_PAYMENT: { label: 'Completed (nothing payable)', hint: 'Nothing was payable. No payment was made.' },
};

// Accounts payment status of an approved voucher (Phase 4)
export const PaymentStatusChip = ({ status, settledVia, size = 'small' }) => {
  if (!status) return null;
  const settled = status === 'PAID' ? SETTLED_VIA_META[settledVia] : null;
  const meta = PAYMENT_STATUS_META[status] || { label: status, color: 'default' };
  return (
    <Chip
      size={size}
      label={settled ? settled.label : meta.label}
      title={settled ? settled.hint : undefined}
      color={meta.color}
      variant={status === 'UNPROCESSED' || settled ? 'outlined' : 'filled'}
    />
  );
};

export const HISTORY_LABELS = {
  CREATED: 'Created voucher',
  UPDATED: 'Draft updated',
  SUBMITTED: 'Submitted to Accounts',
  RESUBMITTED: 'Resubmitted to Accounts',
  SENT_BACK: 'Sent back to employee',
  ROUTED: 'Routed by Accounts',
  APPROVED: 'Approved',
  FINALIZED: 'Finalized by Accounts',
  REJECTED: 'Rejected',
  BILL_UPLOADED: 'Bill uploaded',
  BILL_DELETED: 'Bill deleted',
  DELETED: 'Draft deleted',
  // Phase 4 Accounts processing
  BOOKED: 'Booked by Accounts',
  BOOKING_UPDATED: 'Booking updated',
  PAYMENT_ADDED: 'Payment added',
  PAYMENT_MARKED_PAID: 'Payment marked paid',
  PAYMENT_VOIDED: 'Payment voided',
  ON_HOLD: 'Put on hold',
  RELEASED: 'Released from hold',
  CANCELLED: 'Accounts processing cancelled',
  PAID: 'Fully paid',
  WALLET_POSTED: 'Wallet updated',
  SETTLEMENT_LINKED: 'Settled by settlement voucher',
  WALLET_REVERSED: 'Wallet reversed',
  SETTLEMENT_RELEASED: 'Settlement cancelled - advance released',
};
